import React, {Component, ReactNode} from 'react'
import {StyledPageTitle} from '../reservations/components/CommonStyle'
import {HideInPC, HideInSP} from '../../styledComponents/ResponsiveConvenience'
import PPStepper from '../shared/PPStepper'
import styled from 'styled-components'
import {PrimaryStyledButton, StyledSecondaryButton} from '../../styledComponents/Button'
import {Box} from '@mui/material'
import {votingPreferenceStepsForPc, votingPreferenceStepsForSp} from './ResidentVotingPreferenceMethodSelectionPage'
import ResidentGeneralMeetingRepo, {ResidentProxyVoter} from './ResidentGeneralMeetingRepo'
import {Nullable} from '../../app/TypeHelpers'
import ResidentSectionComponent from '../shared/ResidentSectionComponent'
import ResidentLabeledContentComponent from '../shared/ResidentLabeledContentComponent'
import {SessionStorageHelper} from '../../SessionStorageHelper'
import {
    ResidentLoginInfoSessionStorageKey
} from '../../__NoAutomatedTests/common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {LOGIN_MODE, MESSAGE_TYPE, PROXY_TYPE, VOTING_PREFERENCE_RESPONSE} from '../../CodeMaster'
import MessageDialogComponent from '../../common/resident/MessageDialogComponent'
import {Messages} from '../../Messages'
import {ColumnFlexContainer} from '../../styledComponents/FlexComponents'
import {ResidentVotingPreferenceProxyInputLocationState} from './ResidentVotingPreferenceProxyInputPage'
import {PlainBackLink} from '../../styledComponents/Links'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'
import {Location} from 'react-router-dom'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../shared/hoc/createResidentWithErrorHandlingPage'
import {HttpError} from '../../network/errors/HttpError'
import {FontWeights} from '../../styledComponents/Constants'
import ColonSeparatedItem from '../shared/ColonSeparatedItem'

export interface ResidentVotingPreferenceProxyConfirmationLocationState {
    votingPreferenceId: Nullable<number>
    assocId: number
    assocName: string
    votingPreferenceResponse: VOTING_PREFERENCE_RESPONSE
    votingPreferenceVersion: Nullable<number>
    residentProxyVoter: Nullable<ResidentProxyVoter>
    showRidgeItem: boolean
    beforeNetRespondedAssocIdList: number[]
}

interface Props {
    location: Location<ResidentVotingPreferenceProxyConfirmationLocationState>
    condominiumCd: string
    generalMeetingId: number
    assocId: number
    residentGeneralMeetingRepo: ResidentGeneralMeetingRepo
}

interface State {
    isOpenAdminDialog: boolean
}

class ResidentVotingPreferenceProxyConfirmationPage
    extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        if (!this.props.location.state) {
            this.props.navigate('/resident/error')
        }

        this.state = {
            isOpenAdminDialog: false,
        }

        this.handleConfirmButtonClick = this.handleConfirmButtonClick.bind(this)
        this.handleBackButtonClick = this.handleBackButtonClick.bind(this)
        this.handleAdminDialogOkClick = this.handleAdminDialogOkClick.bind(this)
    }

    isAdminUser(): boolean {
        return SessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_MODE) === LOGIN_MODE.ADMIN.value
    }

    async handleConfirmButtonClick(): Promise<void> {
        if (this.isAdminUser()) {
            this.setState({isOpenAdminDialog: true})
            return
        }

        const {
            condominiumCd,
            generalMeetingId,
            assocId,
            residentGeneralMeetingRepo,
            eventEmitter,
            navigate,
        } = this.props

        const {
            votingPreferenceId,
            votingPreferenceResponse,
            residentProxyVoter,
            votingPreferenceVersion,
            beforeNetRespondedAssocIdList,
        } = this.props.location.state

        await eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                // @ts-ignore
                residentGeneralMeetingRepo.saveGeneralMeetingVotingPreference.bind(
                    residentGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingId,
                    assocId,
                    votingPreferenceId,
                    votingPreferenceResponse,
                    residentProxyVoter,
                    null,
                    votingPreferenceVersion,
                    true,
                ),
                async (httpErrorOrNetworkError) => {
                    if (httpErrorOrNetworkError instanceof HttpError) {
                        navigate(
                            `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`,
                            {
                                state: {
                                    votingPreferenceHttpError: {
                                        status: httpErrorOrNetworkError.status,
                                        responseBody: httpErrorOrNetworkError.responseBody,
                                    },
                                },
                            },
                        )
                    }
                },
            )

            if (!response || response.requestError !== null) {
                return
            }

            navigate(
                `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/complete`,
                {
                    state: {
                        beforeNetRespondedAssocIdList: beforeNetRespondedAssocIdList,
                    },
                },
            )
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleBackButtonClick(): void {
        const {
            condominiumCd,
            generalMeetingId,
            assocId,
            navigate
        } = this.props

        const {
            assocId: stateAssocId,
            assocName,
            votingPreferenceId,
            votingPreferenceResponse,
            residentProxyVoter,
            votingPreferenceVersion,
            beforeNetRespondedAssocIdList,
        } = this.props.location.state

        const backState: ResidentVotingPreferenceProxyInputLocationState = {
            votingPreferenceId: votingPreferenceId,
            assocId: stateAssocId,
            assocName: assocName,
            votingPreferenceResponse: votingPreferenceResponse,
            residentProxyVoter: residentProxyVoter,
            votingPreferenceVersion: votingPreferenceVersion,
            beforeNetRespondedAssocIdList: beforeNetRespondedAssocIdList,
        }

        navigate(
            `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/input/proxy`,
            {
                state: backState,
            },
        )
    }

    handleAdminDialogOkClick(): void {
        this.setState({isOpenAdminDialog: false})
    }

    render(): ReactNode {
        if (!this.props.location.state) {
            return null
        }

        const {
            votingPreferenceId,
            assocId: stateAssocId,
            assocName,
            votingPreferenceResponse,
            residentProxyVoter,
            votingPreferenceVersion,
            showRidgeItem,
            beforeNetRespondedAssocIdList,
        } = this.props.location.state

        const {
            condominiumCd,
            generalMeetingId,
            assocId,
        } = this.props

        if (!residentProxyVoter) {
            return null
        }

        return <>
            <PlainBackLink
                to={`/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/input/proxy`}
                state={{
                    votingPreferenceId,
                    assocId: stateAssocId,
                    assocName,
                    votingPreferenceResponse,
                    residentProxyVoter,
                    votingPreferenceVersion,
                    beforeNetRespondedAssocIdList,
                }}
            >
                戻る
            </PlainBackLink>
            <StyledDisplayItemDividerForPageTop/>
            <HideInSP>
                <PPStepper steps={votingPreferenceStepsForPc} activeStep={2}/>
            </HideInSP>
            <HideInPC>
                <PPStepper steps={votingPreferenceStepsForSp} activeStep={2} alternativeLabel/>
            </HideInPC>
            <Box height={'30px'}/>
            <StyledPageTitle>内容確認</StyledPageTitle>
            <Box height={'20px'}/>
            <div>
                内容をご確認のうえ、意思表明を確定してください。
            </div>
            <Box height={'20px'}/>
            <ContentArea>
                <ResidentSectionComponent
                    content={
                        <ResidentLabeledContentComponent
                            label={'対象管理組合'}
                            labelPosition={'flex-start'}
                            content={<AssocName>{assocName}</AssocName>}
                        />
                    }
                />
                <ResidentSectionComponent
                    content={<ResidentLabeledContentComponent
                        label={'総会参加方法の選択'}
                        labelPosition={'flex-start'}
                        content={<Box fontWeight={'bold'}>
                            {votingPreferenceResponse.label}
                        </Box>}
                    />}
                />
                <ResidentSectionComponent
                    content={<ResidentLabeledContentComponent
                        label={'意思表明'}
                        labelPosition={'flex-start'}
                        content={<>
                            <div>委任先：<b>{
                                PROXY_TYPE.CHAIRMAN.value === residentProxyVoter.proxyType.value
                                    ? '議長へ委任'
                                    : '代理人を指定して委任'
                            }</b></div>
                            {PROXY_TYPE.PROXY_VOTER.value === residentProxyVoter.proxyType.value &&
                                <>
                                    {showRidgeItem && <ColonSeparatedItem label={'棟名'}>
                                        <b>{residentProxyVoter.proxyVoterRidgeName || '棟名なし'}</b>
                                    </ColonSeparatedItem>}
                                    <div>部屋番号：<b>{residentProxyVoter.proxyVoterRoomNumber}</b></div>
                                    <ColonSeparatedItem label={'氏名'}>
                                        <b>{residentProxyVoter.proxyVoterName}</b>
                                    </ColonSeparatedItem>
                                </>
                            }
                        </>}
                    />}
                />
            </ContentArea>
            <Box height={'20px'}/>
            <PrimaryStyledButton onClick={this.handleConfirmButtonClick}>
                確定する
            </PrimaryStyledButton>
            <Box height={'30px'}/>
            <StyledSecondaryButton onClick={this.handleBackButtonClick}>
                戻る
            </StyledSecondaryButton>
            <Box height={'15px'}/>
            <MessageDialogComponent
                open={this.state.isOpenAdminDialog}
                params={{
                    title: '確認',
                    errorLevel: MESSAGE_TYPE.INFORMATION,
                    message: Messages.ISYCR0001.toString(),
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: 'OK',
                            buttonHandler: this.handleAdminDialogOkClick,
                        },
                    ],
                }}
            />
        </>
    }
}

export default createResidentWithErrorHandlingPage(ResidentVotingPreferenceProxyConfirmationPage)

const StyledDisplayItemDividerForPageTop = styled(DisplayItemDividerForPageTop)`
  margin-top: 14px;
  margin-bottom: 15px;
`

const ContentArea = styled(ColumnFlexContainer)`
  row-gap: 5px;
`

const AssocName = styled.div`
  font-weight: ${FontWeights.Bold};
  overflow-wrap: anywhere;
  line-break: anywhere;
`
